import {Component, Input, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {TuiDialogService} from '@taiga-ui/core';
import {PolymorpheusComponent} from '@taiga-ui/polymorpheus';
import {GateweeBulkConfirmDialogComponent} from '@gatewee/components/v2';
import {
  PartnerFormComponent,
  PartnerFormDialogData,
} from './partner-form/partner-form.component';
import {LegalRepresentative} from '@gatewee/types/person';
import {fullNameValidator} from '@gatewee/utils/validators/fullname.validator';
import {validateCPFOrCNPJ} from '@gatewee/utils/validators/cpf-cnpjs-validators';

@Component({
  selector: 'app-partners-form',
  templateUrl: './partners-form.component.html',
  styleUrls: ['./partners-form.component.css'],
  standalone: false,
})
export class PartnersFormComponent implements OnInit {
  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogService: TuiDialogService,
    private readonly translate: TranslateService,
  ) {}
  @Input() formGroup!: FormArray;
  partners: LegalRepresentative[] = [];

  shareSum = 0;

  ngOnInit(): void {
    this.refreshPartners();
  }

  addPartner(partner?: LegalRepresentative): void {
    const formGroup = this.fb.group({
      name: [partner?.name || '', [Validators.required, fullNameValidator]],
      document: [
        partner?.document || '',
        [Validators.required, validateCPFOrCNPJ],
      ],
      email: [partner?.email || '', [Validators.required, Validators.email]],
      phone: [partner?.phone || '', [Validators.required]],
      motherName: [partner?.motherName || ''],
      socialName: [partner?.socialName || ''],
      role: [partner?.role || '', Validators.required],
      birthDate: [partner?.birthDate || '', [Validators.required]],
      address: this.fb.group({
        street: ['', Validators.required],
        number: [''],
        complement: [''],
        neighborhood: ['', Validators.required],
        city: ['', Validators.required],
        state: ['', Validators.required],
        postalCode: ['', Validators.required],
      }),
      politicallyExposedPerson: [false],
      partnerSharePercentage: [100 - this.shareSum, [Validators.required]],
    });

    this.dialogService
      .open<LegalRepresentative | false>(
        new PolymorpheusComponent(PartnerFormComponent),
        {
          data: {formGroup} satisfies PartnerFormDialogData,
          size: 'm',
          dismissible: false,
        },
      )
      .subscribe(result => {
        if (result !== false && result !== null) {
          formGroup.patchValue(result as LegalRepresentative);
          this.formGroup.push(formGroup);
          this.partners.push(result as LegalRepresentative);
        }
        this.refreshPartners();
      });
  }

  editPartner(index: number): void {
    const formGroup = this.formGroup.controls[index] as FormGroup;
    this.dialogService
      .open<LegalRepresentative | false>(
        new PolymorpheusComponent(PartnerFormComponent),
        {
          data: {formGroup} satisfies PartnerFormDialogData,
          size: 'm',
          dismissible: false,
        },
      )
      .subscribe(result => {
        if (result !== false && result !== null) {
          formGroup.patchValue(result as LegalRepresentative);
          this.formGroup.setControl(index, formGroup);
          this.partners[index] = result as LegalRepresentative;
        }
        this.refreshPartners();
      });
  }

  refreshPartners(): void {
    this.formGroup.controls.forEach((control: unknown, idx: number) => {
      this.partners[idx] = (control as FormGroup).value as LegalRepresentative;
    });
  }

  calculateShare(): void {
    this.shareSum = this.partners.reduce(
      (sum, partner) => sum + (partner.partnerSharePercentage || 0),
      0,
    );
  }

  deletePartner(index: number): void {
    this.dialogService
      .open<boolean>(
        new PolymorpheusComponent(GateweeBulkConfirmDialogComponent),
        {
          size: 's',
          dismissible: true,
          data: {
            title: this.translate.instant(
              'features.onboarding.partnersList.confirmDelete.title',
            ),
            purpose: this.translate.instant(
              'features.onboarding.partnersList.confirmDelete.message',
            ),
            selectedCount: 1,
            confirmLabel: this.translate.instant('common.actions.delete'),
            cancelLabel: this.translate.instant('common.actions.cancel'),
          },
        },
      )
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.formGroup.removeAt(index);
        this.partners.splice(index, 1);
      });
  }
}
